import { getAuth, multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

// Generate the TOTP secret
const generateTotpSecret = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error("User not authenticated");
    }

    // Fetch the MFA session at submit time
    const session = await multiFactor(user).getSession();

    const secret = await TotpMultiFactorGenerator.generateSecret(session);

    const qrCodeUrl = secret.generateQrCodeUrl(user.email, "WELNIDO");

    return {
        secret,
        qrCodeUrl,
    };
};

export const useSetTotpMfa = (setTotpSecret) => {
    return useMutation({
        mutationFn: generateTotpSecret,
        retry: false,

        onSuccess: ({ secret, qrCodeUrl }) => {
            setTotpSecret({
                secret,
                qrCodeUrl,
            });
        },

        onError: (err) => {
            if (err.code === "auth/requires-recent-login") {
                toast.error("Please reauthenticate before enabling MFA");
            } else if (err.code === "auth/unverified-email") {
                toast.error("Verify your email address before enabling MFA");
            } else {
                toast.error("Error setting up multi-factor authentication");
            }
        },
    });
};

// Verify the authenticator code and enrol the TOTP factor
const enrolTotpMfa = async ({ secret, code }) => {
    const user = getAuth().currentUser;

    if (!user) {
        throw new Error("User not authenticated");
    }

    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        code,
    );

    await multiFactor(user).enroll(assertion, "Authenticator app");
};

export const useEnrolTotpMfa = (setReauthenticated, onEnrolled) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: enrolTotpMfa,
        retry: false,

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["mfa-factors"],
            });

            toast.success(
                "Authenticator app multi-factor authentication enabled",
            );

            onEnrolled();
        },

        onError: (err) => {
            if (err.code === "auth/requires-recent-login") {
                setReauthenticated(false);
            } else if (err.code === "auth/invalid-verification-code") {
                toast.error("That code is incorrect");
            } else {
                toast.error("Error enabling multi-factor authentication");
            }
        },
    });
};
